import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, X } from 'lucide-react';
import { useClientAuth } from '../context/ClientAuthContext';

/**
 * Petit rappel flottant et discret de l'essai gratuit, qui reste visible pendant tout
 * le scroll des résultats du calculateur (en plus de la grande carte d'inscription).
 * N'apparaît qu'une fois la carte principale hors du champ de vision immédiat, et
 * jamais pour un utilisateur déjà connecté.
 */
export const StickyTrialBanner = ({ language = 'fr' }) => {
  const { isAuthed } = useClientAuth();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const card = document.querySelector('[data-testid="signup-trial-card"]');
      if (!card) { setVisible(false); return; }
      const rect = card.getBoundingClientRect();
      const cardOutOfView = rect.bottom < 80 || rect.top > window.innerHeight - 80;
      setVisible(window.scrollY > 400 && cardOutOfView);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (isAuthed || dismissed || !visible) return null;
  const L = (fr, en) => (language === 'en' ? en : fr);
  const scrollToCard = () => {
    document.querySelector('[data-testid="signup-trial-card"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
        className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-6 sm:bottom-6 z-50 sm:max-w-sm"
        data-testid="sticky-trial-banner"
      >
        <div className="relative flex items-center gap-3 rounded-2xl border border-white/10 bg-gradient-to-r from-zinc-900 to-black shadow-[0_10px_30px_rgba(0,0,0,0.5)] px-4 py-3">
          <button onClick={scrollToCard} className="flex items-center gap-2.5 flex-1 text-left" data-testid="sticky-trial-banner-cta">
            <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-yellow-400 flex items-center justify-center shrink-0">
              <Gift className="w-4.5 h-4.5 text-white" />
            </span>
            <span className="text-sm text-white font-semibold leading-tight">
              {L('7 jours gratuits pour garder vos résultats', '7 days free to keep your results')}
            </span>
          </button>
          <button onClick={() => setDismissed(true)} className="p-1 text-zinc-500 hover:text-white shrink-0" aria-label={L('Fermer', 'Close')} data-testid="sticky-trial-banner-close">
            <X className="w-4 h-4" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default StickyTrialBanner;
